import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';
import { analyticsService } from '../../services/analyticsService';

interface HeaderProps {
  onMenuToggle: () => void;
  showMobileMenu?: boolean;
  isSidebarCollapsed?: boolean;
  className?: string;
}

const Header: React.FC<HeaderProps> = ({ 
  onMenuToggle, 
  showMobileMenu = false, 
  isSidebarCollapsed = false,
  className = '' 
}) => {
  const { user, userProfile, signOut } = useAuth();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isClearingAnalytics, setIsClearingAnalytics] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      // Navigation handled by AuthContext
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleClearAnalyticsClick = () => {
    console.log('Clear Analytics button clicked'); // Debug log
    setShowConfirmDialog(true);
    setIsProfileOpen(false);
  };

  const confirmClearAnalytics = async () => {
    try {
      console.log('Confirming analytics clearing...'); // Debug log
      setIsClearingAnalytics(true);
      setShowConfirmDialog(false);
      
      // Call the clear analytics function
      await analyticsService.clearAllAnalyticsData();
      
      console.log('Analytics cleared successfully'); // Debug log
      
      // Show success message with more detail
      alert('✅ All analytics data cleared successfully!\n\n' + '🔄 What was reset:\n'+ '• All redemption records\n'+ '• User analytics profiles\n'+ '• Revenue and savings data\n'+ '• Analytics events and trends\n'+ '• Dashboard cache\n\n'+ '📊 All metrics should now show 0. Refresh the page to see the changes.');
      
      // Force page refresh to show cleared data
      window.location.reload();
      
    } catch (error) {
      console.error('Error clearing analytics:', error);
      
      // Show detailed error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert('❌ Failed to clear analytics data!\n\n' +
            `Error: ${errorMessage}\n\n` +
            'Please try again or contact support if the issue persists.');
    } finally {
      setIsClearingAnalytics(false);
    }
  };

  const cancelClearAnalytics = () => {
    console.log('Clear Analytics cancelled'); // Debug log
    setShowConfirmDialog(false);
  };

  return (
    <>
      <header className={`fixed top-0 right-0 z-40 bg-background border-b border-border transition-all duration-300 ${
        isSidebarCollapsed ? 'left-16' : 'left-0 lg:left-64'
      } ${className}`}>
        <div className="flex items-center justify-between px-4 lg:px-6 h-16">
          {/* Mobile Menu Button */}
          <button
            onClick={onMenuToggle}
            className="lg:hidden p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Toggle mobile menu"
          >
            <Icon name={showMobileMenu ? "X" : "Menu"} size={20} />
          </button>

          {/* Desktop Logo/Title - Only show when sidebar is collapsed */}
          <div className={`hidden lg:flex items-center ${isSidebarCollapsed ? 'block' : 'hidden'}`}>
            <h2 className="text-lg font-semibold text-foreground">
              TravelHeroes Admin
            </h2>
          </div>

          {/* Spacer when sidebar is expanded */}
          {!isSidebarCollapsed && <div className="hidden lg:block" />}

          {/* Right Side - User Profile */}
          <div className="flex items-center space-x-4">
            {/* Notifications */}
            <Button variant="ghost" size="sm" iconName="Bell">
              <span className="sr-only">Notifications</span>
            </Button>

            {/* User Profile Dropdown */}
            {user && (
              <div className="relative">
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="flex items-center space-x-3 p-2 hover:bg-muted rounded-lg transition-colors"
                >
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    {userProfile?.avatar_url ? (
                      <img
                        src={userProfile.avatar_url}
                        alt={userProfile.full_name || user.email || 'User'}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <Icon name="User" size={16} className="text-primary" />
                    )}
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-foreground">
                      {userProfile?.full_name || user.email}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {userProfile?.role || 'User'}
                    </p>
                  </div>
                  <Icon name="ChevronDown" size={16} className="text-muted-foreground" />
                </button>

                {/* Profile Dropdown */}
                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-background border border-border rounded-lg shadow-lg py-1 z-50">
                    <div className="px-4 py-2 border-b border-border">
                      <p className="text-sm font-medium text-foreground">
                        {userProfile?.full_name || user.email}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {user.email}
                      </p>
                    </div>
                    
                    <button
                      onClick={() => {
                        setIsProfileOpen(false);
                        // Navigate to profile page
                      }}
                      className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                    >
                      <Icon name="User" size={16} />
                      <span>Profile</span>
                    </button>
                    
                    <button
                      onClick={() => {
                        setIsProfileOpen(false);
                        // Navigate to settings page
                      }}
                      className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                    >
                      <Icon name="Settings" size={16} />
                      <span>Settings</span>
                    </button>
                    
                    {/* Clear Analytics Button - Enhanced with better visibility */}
                    <div className="border-t border-border mt-1 pt-1">
                      <button
                        onClick={handleClearAnalyticsClick}
                        disabled={isClearingAnalytics}
                        className="flex items-center space-x-2 w-full px-4 py-3 text-sm text-orange-600 hover:bg-orange-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                        title="Clear all analytics data (Testing purposes)"
                      >
                        <Icon name={isClearingAnalytics ? "Loader2" : "Trash2"} size={16} className={isClearingAnalytics ? "animate-spin" : ""} />
                        <span>{isClearingAnalytics ? 'Clearing...' : '🧹 Clear Analytics'}</span>
                      </button>
                    </div>
                    
                    <div className="border-t border-border mt-1 pt-1">
                      <button
                        onClick={handleSignOut}
                        className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-destructive hover:bg-muted transition-colors"
                      >
                        <Icon name="LogOut" size={16} />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Login Button for Non-authenticated Users */}
            {!user && (
              <Button variant="outline" size="sm" iconName="LogIn">
                Login
              </Button>
            )}
          </div>
        </div>

        {/* Close dropdown when clicking outside */}
        {isProfileOpen && (
          <div
            className="fixed inset-0 z-30"
            onClick={() => setIsProfileOpen(false)}
          />
        )}
      </header>

      {/* Enhanced Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-background border border-border rounded-lg shadow-2xl p-6 max-w-lg w-full mx-4 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Icon name="AlertTriangle" size={24} className="text-orange-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-foreground">⚠️ Clear All Analytics Data</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone!</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-foreground mb-4 font-medium">
                This will permanently delete ALL analytics data:
              </p>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <ul className="text-sm text-orange-800 space-y-2">
                  <li className="flex items-center space-x-2">
                    <Icon name="Trash2" size={14} />
                    <span>All redemption records and history</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Icon name="Users" size={14} />
                    <span>User analytics profiles and statistics</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Icon name="DollarSign" size={14} />
                    <span>Revenue, savings, and financial data</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Icon name="TrendingUp" size={14} />
                    <span>Charts, trends, and dashboard metrics</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Icon name="Database" size={14} />
                    <span>Cached analytics and aggregated data</span>
                  </li>
                </ul>
              </div>
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 font-semibold">
                  ⚠️ All dashboard metrics will reset to 0
                </p>
                <p className="text-xs text-red-600 mt-1">
                  This is intended for testing purposes only
                </p>
              </div>
            </div>

            <div className="flex space-x-3 justify-end">
              <Button 
                variant="outline" 
                onClick={cancelClearAnalytics}
                className="px-6 py-2"
                disabled={isClearingAnalytics}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={confirmClearAnalytics}
                disabled={isClearingAnalytics}
                className="px-6 py-2 bg-red-600 hover:bg-red-700"
              >
                {isClearingAnalytics ? (
                  <>
                    <Icon name="Loader2" size={16} className="animate-spin mr-2" />
                    Clearing...
                  </>
                ) : (
                  <>
                    <Icon name="Trash2" size={16} className="mr-2" />
                    Yes, Clear Everything
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;